/**
 * The reviewer markup overlay (Phase 7) - an SVG sized exactly over the current PDF page
 * (see PdfViewer.tsx's renderOverlay slot), drawing in normalised 0-1 coordinates so it
 * stays aligned regardless of zoom. Handles both rendering existing annotations for this
 * page and capturing new ones for the active tool.
 */
import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { AnnotationRow, PointCoordinates, RectCoordinates } from '../../reviewer/types';
import type { MarkupTool } from './MarkupToolbar';

const AMBER = '#B7791F';
const MIN_SIZE = 0.01; // ignore accidental micro-drags

interface MarkupLayerProps {
  page: number;
  annotations: AnnotationRow[];
  numberById: Map<string, number>;
  activeTool: MarkupTool;
  onCreate: (input: { page: number; type: 'highlight' | 'note' | 'shape'; coordinates: unknown }) => void;
  onMarkerClick: (annotationId: string) => void;
}

type DragState =
  | { kind: 'rect'; start: PointCoordinates; current: PointCoordinates }
  | { kind: 'freehand'; points: PointCoordinates[] }
  | null;

function clientToNormalised(svg: SVGSVGElement, clientX: number, clientY: number): PointCoordinates {
  const rect = svg.getBoundingClientRect();
  const x = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
  const y = rect.height > 0 ? (clientY - rect.top) / rect.height : 0;
  return { x: Math.min(Math.max(x, 0), 1), y: Math.min(Math.max(y, 0), 1) };
}

function rectFrom(a: PointCoordinates, b: PointCoordinates): RectCoordinates {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}

export function MarkupLayer({ page, annotations, numberById, activeTool, onCreate, onMarkerClick }: MarkupLayerProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [drag, setDrag] = useState<DragState>(null);

  const pageAnnotations = annotations.filter((a) => a.page === page);
  const drawing = activeTool !== 'none';

  function handlePointerDown(e: ReactPointerEvent<SVGSVGElement>) {
    if (!svgRef.current) return;
    const point = clientToNormalised(svgRef.current, e.clientX, e.clientY);

    if (activeTool === 'note') {
      onCreate({ page, type: 'note', coordinates: point });
      return;
    }
    if (activeTool === 'highlight' || activeTool === 'rectangle') {
      setDrag({ kind: 'rect', start: point, current: point });
      svgRef.current.setPointerCapture(e.pointerId);
    } else if (activeTool === 'freehand') {
      setDrag({ kind: 'freehand', points: [point] });
      svgRef.current.setPointerCapture(e.pointerId);
    }
  }

  function handlePointerMove(e: ReactPointerEvent<SVGSVGElement>) {
    if (!drag || !svgRef.current) return;
    const point = clientToNormalised(svgRef.current, e.clientX, e.clientY);
    if (drag.kind === 'rect') {
      setDrag({ ...drag, current: point });
    } else {
      setDrag({ kind: 'freehand', points: [...drag.points, point] });
    }
  }

  function handlePointerUp() {
    if (!drag) return;
    if (drag.kind === 'rect') {
      const rect = rectFrom(drag.start, drag.current);
      if (rect.w >= MIN_SIZE && rect.h >= MIN_SIZE) {
        onCreate({ page, type: activeTool === 'highlight' ? 'highlight' : 'shape', coordinates: activeTool === 'highlight' ? rect : { kind: 'rectangle', ...rect } });
      }
    } else if (drag.points.length >= 2) {
      onCreate({ page, type: 'shape', coordinates: { kind: 'freehand', points: drag.points } });
    }
    setDrag(null);
  }

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      className="markup-layer"
      style={{ pointerEvents: drawing ? 'auto' : 'none', cursor: drawing ? 'crosshair' : 'default' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      {pageAnnotations.map((a) => {
        const number = numberById.get(a.id);
        if (a.type === 'highlight') {
          const c = a.coordinates as RectCoordinates;
          return <rect key={a.id} x={c.x} y={c.y} width={c.w} height={c.h} fill={AMBER} fillOpacity={0.32} />;
        }
        if (a.type === 'shape') {
          const c = a.coordinates as RectCoordinates & { kind?: string; points?: PointCoordinates[] };
          if (c.kind === 'freehand' && c.points) {
            return (
              <polyline
                key={a.id}
                points={c.points.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={AMBER}
                strokeWidth={0.005}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          }
          return (
            <rect
              key={a.id}
              x={c.x}
              y={c.y}
              width={c.w}
              height={c.h}
              fill="none"
              stroke={AMBER}
              strokeWidth={0.004}
            />
          );
        }
        // note
        const c = a.coordinates as PointCoordinates;
        return (
          <g
            key={a.id}
            transform={`translate(${c.x} ${c.y})`}
            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
            onPointerDown={(e) => {
              e.stopPropagation();
              onMarkerClick(a.id);
            }}
          >
            <circle r={0.014} fill={AMBER} />
            <text
              x={0}
              y={0.005}
              fontSize={0.014}
              fill="#fff"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, userSelect: 'none' }}
            >
              {number ?? ''}
            </text>
          </g>
        );
      })}

      {drag?.kind === 'rect'
        ? (() => {
            const rect = rectFrom(drag.start, drag.current);
            return (
              <rect
                x={rect.x}
                y={rect.y}
                width={rect.w}
                height={rect.h}
                fill={activeTool === 'highlight' ? AMBER : 'none'}
                fillOpacity={activeTool === 'highlight' ? 0.32 : undefined}
                stroke={AMBER}
                strokeWidth={0.004}
                strokeDasharray={activeTool === 'highlight' ? undefined : '0.01'}
              />
            );
          })()
        : null}
      {drag?.kind === 'freehand' ? (
        <polyline
          points={drag.points.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke={AMBER}
          strokeWidth={0.005}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : null}
    </svg>
  );
}
